"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.UpdateApiKeyDriver = void 0;
const tslib_1 = require("tslib");
const hooks_1 = require("@feathersjs/hooks");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const typedi_1 = require("typedi");
const teamsGraphClient_1 = require("../../../client/teamsGraphClient");
const constants_1 = require("../../../common/constants");
const localizeUtils_1 = require("../../../common/localizeUtils");
const error_1 = require("../../../error");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const ApiSecretRegistration_1 = require("../teamsApp/interfaces/ApiSecretRegistration");
const apiKeyNameTooLong_1 = require("./error/apiKeyNameTooLong");
const constants_2 = require("./utility/constants");
const utility_1 = require("./utility/utility");
const wrapUtil_1 = require("../util/wrapUtil");
const actionName = "apiKey/update"; // DO NOT MODIFY the name
const helpLink = "https://aka.ms/teamsfx-actions/apiKey-update";
let UpdateApiKeyDriver = class UpdateApiKeyDriver {
    description = (0, localizeUtils_1.getLocalizedString)("driver.apiKey.description.update");
    progressTitle = (0, localizeUtils_1.getLocalizedString)("driver.aadApp.apiKey.title.update");
    async execute(args, context) {
        const wrapDriverContext = new wrapUtil_1.WrapDriverContext(context, actionName, actionName);
        const result = await this._run(args, wrapDriverContext);
        return result;
    }
    async _run(args, context) {
        const summaries = [];
        const outputs = new Map();
        try {
            context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.startExecuteDriver, actionName));
            this.validateArgs(args);
            let domains = [];
            if (args.baseUrl) {
                domains = [args.baseUrl];
            }
            else {
                domains = await (0, utility_1.getDomain)(args, context, actionName);
                (0, utility_1.validateDomain)(domains, actionName);
            }
            const appStudioTokenRes = await context.m365TokenProvider.getAccessToken({
                scopes: (0, constants_1.TeamsGraphScopes)(),
            });
            if (appStudioTokenRes.isErr()) {
                throw appStudioTokenRes.error;
            }
            const appStudioToken = appStudioTokenRes.value;
            const getApiKeyRes = await teamsGraphClient_1.teamsGraphClient.getApiKeyRegistrationById(appStudioToken, args.registrationId);
            const diffMsgs = this.compareApiKeyRegistration(getApiKeyRes, args, domains);
            // If there is no difference, skip the update
            if (!diffMsgs || diffMsgs.length === 0) {
                const summary = (0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.skipUpdateApiKey);
                context.logProvider?.info(summary);
                summaries.push(summary);
                return {
                    result: (0, teamsfx_api_1.ok)(outputs),
                    summaries: summaries,
                };
            }
            // If there is difference, ask user to confirm the update
            // Skip confirm if only targetUrlsShouldStartWith is different when the url contains devtunnel
            if (!this.shouldSkipConfirm(diffMsgs, getApiKeyRes.targetUrlsShouldStartWith, domains)) {
                const userConfirm = await context.ui.confirm({
                    name: "confirm-update-api-key",
                    title: (0, localizeUtils_1.getLocalizedString)("driver.apiKey.confirm.update", diffMsgs.join(",\n")),
                    default: true,
                });
                if (userConfirm.isErr()) {
                    throw userConfirm.error;
                }
            }
            const apiKey = this.mapArgsToApiSecretRegistration(args, domains);
            await teamsGraphClient_1.teamsGraphClient.updateApiKeyRegistration(appStudioToken, apiKey, args.registrationId);
            void context.ui.showMessage("info", (0, localizeUtils_1.getLocalizedString)("driver.apiKey.info.update", diffMsgs.join(",\n")), false);
            const summary = (0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.successUpdateApiKey);
            context.logProvider?.info(summary);
            summaries.push(summary);
            return {
                result: (0, teamsfx_api_1.ok)(outputs),
                summaries: summaries,
            };
        }
        catch (error) {
            if (error instanceof teamsfx_api_1.UserError || error instanceof teamsfx_api_1.SystemError) {
                context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.failedExecuteDriver, actionName, error.displayMessage));
                return {
                    result: (0, teamsfx_api_1.err)(error),
                    summaries: summaries,
                };
            }
            const message = JSON.stringify(error);
            context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.failedExecuteDriver, actionName, message));
            return {
                result: (0, teamsfx_api_1.err)((0, error_1.assembleError)(error, actionName)),
                summaries: summaries,
            };
        }
    }
    validateArgs(args) {
        const invalidParameters = [];
        if (typeof args.registrationId !== "string" || !args.registrationId) {
            invalidParameters.push("registrationId");
        }
        if (typeof args.name !== "string" || !args.name) {
            invalidParameters.push("name");
        }
        if (args.name.length > 128) {
            throw new apiKeyNameTooLong_1.ApiKeyNameTooLongError(actionName);
        }
        if (typeof args.appId !== "string" || !args.appId) {
            invalidParameters.push("appId");
        }
        if (args.baseUrl && (typeof args.baseUrl !== "string" || !(0, utility_1.validateUrl)(args.baseUrl))) {
            invalidParameters.push("baseUrl");
        }
        if (args.apiSpecPath && typeof args.apiSpecPath !== "string") {
            invalidParameters.push("apiSpecPath");
        }
        if (!args.baseUrl && !args.apiSpecPath) {
            invalidParameters.push("baseUrl");
            invalidParameters.push("apiSpecPath");
        }
        if (args.applicableToApps &&
            args.applicableToApps !== ApiSecretRegistration_1.ApiSecretRegistrationAppType.AnyApp &&
            args.applicableToApps !== ApiSecretRegistration_1.ApiSecretRegistrationAppType.SpecificApp) {
            invalidParameters.push("applicableToApps");
        }
        if (args.targetAudience &&
            args.targetAudience !== ApiSecretRegistration_1.ApiSecretRegistrationTargetAudience.AnyTenant &&
            args.targetAudience !== ApiSecretRegistration_1.ApiSecretRegistrationTargetAudience.HomeTenant) {
            invalidParameters.push("targetAudience");
        }
        if (invalidParameters.length > 0) {
            throw new error_1.InvalidActionInputError(actionName, invalidParameters, helpLink);
        }
    }
    compareApiKeyRegistration(current, input, domain) {
        const diffMsgs = [];
        if (current.description !== input.name) {
            diffMsgs.push(`description: ${current.description} => ${input.name}`);
        }
        if (input.applicableToApps && current.applicableToApps !== input.applicableToApps) {
            let msg = `applicableToApps: ${current.applicableToApps} => ${input.applicableToApps}`;
            if (input.applicableToApps === "SpecificApp") {
                msg += `, specificAppId: ${input.appId}`;
            }
            diffMsgs.push(msg);
        }
        if (input.targetAudience && current.targetAudience !== input.targetAudience) {
            diffMsgs.push(`targetAudience: ${current.targetAudience} => ${input.targetAudience}`);
        }
        // Compare domain
        if (current.targetUrlsShouldStartWith.length !== domain.length ||
            !current.targetUrlsShouldStartWith.every((value) => domain.includes(value)) ||
            !domain.every((value) => current.targetUrlsShouldStartWith.includes(value))) {
            diffMsgs.push(`targetUrlsShouldStartWith: ${current.targetUrlsShouldStartWith.join(",")} => ${domain.join(",")}`);
        }
        return diffMsgs;
    }
    mapArgsToApiSecretRegistration(args, domain) {
        const targetAudience = args.targetAudience
            ? args.targetAudience
            : undefined;
        const applicableToApps = args.applicableToApps
            ? args.applicableToApps
            : undefined;
        const apiKey = {
            description: args.name,
            targetUrlsShouldStartWith: domain,
            applicableToApps: applicableToApps,
            specificAppId: applicableToApps === ApiSecretRegistration_1.ApiSecretRegistrationAppType.SpecificApp ? args.appId : "",
            targetAudience: targetAudience,
        };
        return apiKey;
    }
    // Should skip confirm box if only targetUrlsShouldStartWith is different and the url contains devtunnel
    shouldSkipConfirm(diffMsgs, getDomain, domain) {
        return (diffMsgs.length === 1 &&
            diffMsgs[0].includes("targetUrlsShouldStartWith") &&
            getDomain.length === domain.length &&
            getDomain.every((value) => value.includes("devtunnel")) &&
            domain.every((value) => value.includes("devtunnel")));
    }
};
exports.UpdateApiKeyDriver = UpdateApiKeyDriver;
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, addStartAndEndTelemetry_1.addStartAndEndTelemetry)(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, wrapUtil_1.WrapDriverContext]),
    tslib_1.__metadata("design:returntype", Promise)
], UpdateApiKeyDriver.prototype, "_run", null);
exports.UpdateApiKeyDriver = UpdateApiKeyDriver = tslib_1.__decorate([
    (0, typedi_1.Service)(actionName) // DO NOT MODIFY the service name
], UpdateApiKeyDriver);
//# sourceMappingURL=update.js.map